'use client';

import { Button } from '@components/ui/button';
import { usePwa } from '@hooks/use-pwa';
import { cn } from '@utils/cn';
import { useTranslations } from 'next-intl';

/**
 * Non-blocking offline notice. Zero-backed screens keep showing cached rows;
 * this banner explains why live sync may be paused.
 */
export function OfflineBanner({ className }: { className?: string }) {
  const { online, slowConnection } = usePwa();
  const t = useTranslations('pwa');

  if (online && !slowConnection) return null;

  return (
    <div
      role="status"
      className={cn(
        'fixed inset-x-0 top-[var(--safe-top)] z-50 mx-auto max-w-lg px-3 pt-2 standalone:block',
        className
      )}
    >
      {/* Status chrome stays neutral; the warning dot carries the meaning. */}
      <div className="flex items-center justify-center gap-2 rounded-full bg-card px-4 py-2 text-label text-secondary-foreground shadow-[inset_0_0_0_1px_var(--border-strong)] backdrop-blur-sm">
        <span className="size-1.5 shrink-0 rounded-full bg-status-warning" />
        {online ? t('slow') : t('offline')}
      </div>
    </div>
  );
}

/**
 * In-flow reload prompt for a waiting service worker. Lives in the app
 * shell (not `position: fixed`) so it cannot cover header circles or jump
 * onto the messenger composer when the keyboard opens.
 */
export function PwaUpdateBanner({ className }: { className?: string }) {
  const { updateAvailable, applyUpdate } = usePwa();
  const t = useTranslations('pwa');

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      className={cn(
        'shrink-0 px-3 pb-2 pt-1 desktop-chrome:px-7 desktop-chrome:pt-3',
        className
      )}
    >
      <div className="mx-auto flex max-w-lg items-center gap-3 rounded-xl border border-border-strong bg-card px-3 py-2 shadow-md">
        <span className="size-2 shrink-0 rounded-full bg-primary" aria-hidden />
        <p className="min-w-0 flex-1 text-body-dense text-foreground">
          {t('update-available')}
        </p>
        <Button variant="primary" size="sm" onClick={applyUpdate}>
          {t('reload')}
        </Button>
      </div>
    </div>
  );
}
